import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Job Selection Step
 *
 * Second step of mobile onboarding - user selects a job template
 * or chooses to create a custom job.
 */
import { useState } from 'react';
import { Box, Typography, Card, Menu, MenuItem, ListItemIcon, ListItemText, } from '@mui/material';
import { ArrowForward as ArrowForwardIcon, KeyboardArrowDown as KeyboardArrowDownIcon, Check as CheckIcon, } from '@mui/icons-material';
import { INDUSTRY_CONFIGS } from '../../types/onboarding';
// ============ Sub-Components ============
const IndustrySelector = ({ selectedIndustry, onIndustryChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const industryConfig = INDUSTRY_CONFIGS[selectedIndustry];
    const industries = Object.values(INDUSTRY_CONFIGS);
    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };
    const handleSelectIndustry = (industry) => {
        onIndustryChange(industry);
        handleCloseMenu();
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { onClick: handleOpenMenu, sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                    cursor: 'pointer',
                    py: 5,
                    transition: 'all 0.15s',
                    '&:hover': {
                        color: 'text.primary',
                    },
                }, children: [_jsxs(Typography, { variant: "h5", fontWeight: 500, children: [industryConfig.icon, " ", industryConfig.name] }), _jsx(KeyboardArrowDownIcon, { fontSize: "small" })] }), _jsx(Menu, { anchorEl: anchorEl, open: Boolean(anchorEl), onClose: handleCloseMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, transformOrigin: { vertical: 'top', horizontal: 'center' }, PaperProps: {
                    sx: {
                        minWidth: 240,
                        mt: 1,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    },
                }, children: industries.map((industry) => (_jsxs(MenuItem, { onClick: () => handleSelectIndustry(industry.id), selected: selectedIndustry === industry.id, sx: {
                        py: 1.5,
                        '&.Mui-selected': { bgcolor: 'primary.light' },
                    }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36, fontSize: '1.2rem' }, children: industry.icon }), _jsx(ListItemText, { primary: industry.name }), selectedIndustry === industry.id && (_jsx(CheckIcon, { sx: { color: 'primary.main', fontSize: '1.25rem' } }))] }, industry.id))) })] }));
};
const TemplateListItem = ({ template, onClick }) => (_jsxs(Card, { elevation: 0, onClick: onClick, sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 3,
        mb: 4,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        transition: 'all 0.15s',
        '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'grey.50',
        },
        '&:active': { bgcolor: 'grey.100' },
    }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 500, color: 'text.primary' }, children: template.title }), _jsx(ArrowForwardIcon, { sx: { color: 'primary.main', fontSize: '1.25rem' } })] }));
const CreateNewJobOption = ({ onClick }) => (_jsx(Box, { onClick: onClick, sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        mb: 2,
        cursor: 'pointer',
    }, children: _jsx(Typography, { variant: "body2", sx: {
            fontWeight: 500,
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
        }, children: "Create new job" }) }));
// ============ Main Component ============
export const JobSelectionStep = ({ industryContext, templates, onSelectTemplate, onCreateCustom, onIndustryChange, }) => {
    return (_jsxs(Box, { sx: { px: 4, py: 3 }, children: [_jsx(Typography, { variant: "h2", children: "Start with a job" }), _jsx(IndustrySelector, { selectedIndustry: industryContext, onIndustryChange: onIndustryChange }), _jsxs(Box, { sx: { mt: 2 }, children: [templates.map((template) => (_jsx(TemplateListItem, { template: template, onClick: () => onSelectTemplate(template) }, template.id))), _jsx(CreateNewJobOption, { onClick: onCreateCustom })] })] }));
};
